import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import apiService, { WORKING_ORG_STORAGE_KEY } from '../services/api.service';
import { useAuth } from './AuthContext';

export interface WorkingOrganization {
  id: string;
  name: string;
  slug?: string;
}

interface OrgContextType {
  /** Organisations disponibles — vide pour un non-admin. */
  organizations: WorkingOrganization[];
  /** `null` = aucune organisation de travail choisie. */
  workingOrgId: string | null;
  workingOrg: WorkingOrganization | null;
  setWorkingOrgId: (id: string | null) => void;
  /** Un admin sans organisation de travail ne peut pas ouvrir les écrans « organisation ». */
  needsOrganization: boolean;
  isAdmin: boolean;
  loading: boolean;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

/**
 * L'organisation sur laquelle un ADMIN travaille.
 *
 * Un admin n'organise pas, il administre : son compte n'a pas vocation à porter
 * une organisation, et les écrans « organisation » (Paramètres, TVA, Stripe,
 * Sync, jetons) doivent donc lui demander LAQUELLE avant d'agir — au lieu de
 * retomber en silence sur celle de son propre compte, ce qui lui faisait
 * modifier les réglages d'une organisation pendant qu'il travaillait sur
 * l'événement d'une autre.
 *
 * Un organisateur ne voit jamais ce mécanisme : il n'a qu'une organisation, la
 * sienne, et rien n'est envoyé au serveur.
 */
export const OrgProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [organizations, setOrganizations] = useState<WorkingOrganization[]>([]);
  // Lu en SYNCHRONE au premier rendu, comme `selectedEventId` dans
  // EventContext. Layout rend la page sous `key={workingOrgId}` : si le choix
  // sauvegardé n'arrivait qu'après le chargement des organisations, la clé
  // passait de null à l'id et la page était DÉMONTÉE puis remontée à chaque
  // arrivée — un lien profond (`?user=` d'une notification) ouvrait la fiche,
  // le remontage l'emportait, et le paramètre déjà consommé ne la rouvrait
  // pas (constat de Bryan sur staging, 2026-09-28). La liste chargée ensuite
  // ne fait que VALIDER ce choix : inchangé s'il existe encore, sinon abandonné.
  // Un non-admin ne peut pas hériter d'un choix : l'effet ci-dessous le purge
  // dès que le rôle est connu, et la déconnexion passe par là aussi.
  const [workingOrgId, setWorkingOrgIdState] = useState<string | null>(() => {
    const sauvegarde = localStorage.getItem(WORKING_ORG_STORAGE_KEY);
    apiService.setWorkingOrganizationId(sauvegarde);
    return sauvegarde;
  });
  const [loading, setLoading] = useState(false);

  const setWorkingOrgId = useCallback((id: string | null) => {
    setWorkingOrgIdState(id);
    // L'en-tête est posé par l'intercepteur : il doit connaître le choix AVANT
    // la première requête qui suit, d'où la mise à jour synchrone.
    apiService.setWorkingOrganizationId(id);
    if (id) localStorage.setItem(WORKING_ORG_STORAGE_KEY, id);
    else localStorage.removeItem(WORKING_ORG_STORAGE_KEY);
  }, []);

  useEffect(() => {
    // Un non-admin ne doit JAMAIS émettre l'en-tête (le serveur refuse en 403) :
    // on purge aussi un choix hérité d'une session admin précédente.
    if (!isAdmin) {
      setOrganizations([]);
      setWorkingOrgIdState(null);
      apiService.setWorkingOrganizationId(null);
      localStorage.removeItem(WORKING_ORG_STORAGE_KEY);
      return;
    }

    let annule = false;
    setLoading(true);
    apiService
      .getOrganizations()
      .then((data: any) => {
        if (annule) return;
        const liste: WorkingOrganization[] = (data?.organizations || data || [])
          .map((o: any) => ({ id: o.id, name: o.name, slug: o.slug }))
          .sort((a: WorkingOrganization, b: WorkingOrganization) => a.name.localeCompare(b.name));
        setOrganizations(liste);

        // Un choix sauvegardé qui ne désigne plus rien est abandonné, jamais
        // remplacé en silence : l'admin doit revoir sur quoi il travaille.
        const sauvegarde = localStorage.getItem(WORKING_ORG_STORAGE_KEY);
        if (sauvegarde && liste.some((o) => o.id === sauvegarde)) setWorkingOrgId(sauvegarde);
        else setWorkingOrgId(null);
      })
      .catch((e) => console.error('Chargement des organisations impossible:', e))
      .finally(() => !annule && setLoading(false));

    return () => {
      annule = true;
    };
  }, [isAdmin, setWorkingOrgId]);

  const workingOrg = useMemo(
    () => organizations.find((o) => o.id === workingOrgId) || null,
    [organizations, workingOrgId]
  );

  const value: OrgContextType = {
    organizations,
    workingOrgId,
    workingOrg,
    setWorkingOrgId,
    needsOrganization: isAdmin && !workingOrgId,
    isAdmin,
    loading,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export const useOrg = (): OrgContextType => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg doit être utilisé dans un OrgProvider');
  return ctx;
};
